/**
 * Product formatting utilities for displaying weight/volume and other product metadata
 */

export interface WeightVolumeInfo {
  weight?: string;
  unit?: string;
  drainedWeight?: string;
  drainedUnit?: string;
}

/**
 * Extracts weight/volume information from raw receipt line text
 * Handles:
 * - Main weight/volume: e.g., "500g", "1.5kg", "250ml", "2L"
 * - Drained weight: e.g., "(280g drained)", "(180g drained weight)"
 * 
 * @param rawLineText - The full original line from the receipt
 * @returns Object containing weight, unit, and optional drained weight/unit
 * 
 * @example
 * extractWeightAndVolume("0.486kg Sainsbury's Organic Eggs £2.50")
 * // Returns: { weight: "0.486", unit: "kg" }
 * 
 * @example
 * extractWeightAndVolume("250ml (180g drained) Beans in Tomato Sauce £0.65")
 * // Returns: { weight: "250", unit: "ml", drainedWeight: "180", drainedUnit: "g" }
 */
export function extractWeightAndVolume(rawLineText: string): WeightVolumeInfo {
  if (!rawLineText) return {};

  const result: WeightVolumeInfo = {};

  // Match main weight/volume: e.g., "500g", "1.5kg", "250ml", "2L"
  const mainMatch = rawLineText.match(/(\d+(?:\.\d+)?)\s*(g|kg|ml|l)\b/i);
  if (mainMatch) {
    result.weight = mainMatch[1];
    result.unit = mainMatch[2].toLowerCase();
  }

  // Match drained weight in parentheses: e.g., "(280g drained)", "(180g drained weight)"
  const drainedMatch = rawLineText.match(/\((\d+(?:\.\d+)?)\s*(g|kg|ml|l)\s*(?:drained|drain)\b/i);
  if (drainedMatch) {
    result.drainedWeight = drainedMatch[1];
    result.drainedUnit = drainedMatch[2].toLowerCase();
  }

  return result;
}

/**
 * Formats weight/volume information for display
 * 
 * @param info - Weight/volume info object from extractWeightAndVolume
 * @returns Formatted string like "500g" or "500g (280g drained)"
 * 
 * @example
 * formatWeightAndVolume({ weight: "500", unit: "g" })
 * // Returns: "500g"
 * 
 * @example
 * formatWeightAndVolume({
 *   weight: "250",
 *   unit: "ml",
 *   drainedWeight: "180",
 *   drainedUnit: "g"
 * })
 * // Returns: "250ml (180g drained)"
 */
export function formatWeightAndVolume(info: WeightVolumeInfo): string {
  if (!info.weight) return '';

  let formatted = `${info.weight}${info.unit}`;

  if (info.drainedWeight) {
    formatted += ` (${info.drainedWeight}${info.drainedUnit} drained)`;
  }

  return formatted;
}

/**
 * Extracts and formats weight/volume in one step (convenience function)
 * 
 * @param rawLineText - The full original line from the receipt
 * @returns Formatted weight/volume string
 * 
 * @example
 * getFormattedWeight("250ml (180g drained) Beans in Tomato Sauce £0.65")
 * // Returns: "250ml (180g drained)"
 */
export function getFormattedWeight(rawLineText: string): string {
  const info = extractWeightAndVolume(rawLineText);
  return formatWeightAndVolume(info);
}
